import { useDispatch, useSelector } from 'react-redux';
import { showToast } from 'src/store/toast';
import {
  allCodesetsReceived,
  selectAllCodesets,
  selectRootOrganizationId,
  selectDataSourceId,
} from 'src/store/create-cohort';
import { getCodesetsByOrganizationAndDatasource } from 'src/api/codesets';
import { useCreateCodesetMutation, useUpdateCodesetMutation } from 'src/api/create-cohort/mutation';
import { ConceptCode } from '../types';

export const useCodesetManagement = () => {
  const dispatch = useDispatch();
  const allCodesets = useSelector(selectAllCodesets);
  const organizationId = useSelector(selectRootOrganizationId);
  const datasourceId = useSelector(selectDataSourceId);

  const { mutateAsync: createCodeset } = useCreateCodesetMutation();
  const { mutateAsync: updateCodeset } = useUpdateCodesetMutation();

  const refetchCodesets = async () => {
    if (organizationId && datasourceId) {
      const response = await getCodesetsByOrganizationAndDatasource(organizationId, datasourceId);
      dispatch(allCodesetsReceived(response.codesets));
    }
  };

  const saveCodeset = async (
    isUpdate: boolean,
    codesetId: string | undefined,
    codesetData: {
      name: string;
      description: string;
      table_name: string;
      search_term: string;
      items: ConceptCode[];
      enabledIds: string[];
    },
  ) => {
    if (!organizationId || !datasourceId) {
      dispatch(
        showToast({
          type: 'dangerous',
          message: 'Organization ID or Datasource ID is missing',
        }),
      );
      throw new Error('Missing required IDs');
    }

    const dataPayload = codesetData.items
      .filter((x) => codesetData.enabledIds.includes(x.concept_id))
      .map((x) => ({
        ...x,
        relevance: parseFloat(x.relevance.replace('%', '')) / 100,
      }));

    if (isUpdate && codesetId) {
      return await updateCodeset({
        id: codesetId,
        organizationId,
        datasourceId,
        updateDto: {
          codesetName: codesetData.name,
          codesetDescription: codesetData.description,
          data: dataPayload,
        },
      });
    } else {
      return await createCodeset({
        organizationId,
        datasourceId,
        table_name: codesetData.table_name,
        search_term: codesetData.search_term,
        codesetName: codesetData.name,
        codesetDescription: codesetData.description,
        data: dataPayload,
      });
    }
  };

  return {
    allCodesets,
    organizationId,
    datasourceId,
    refetchCodesets,
    saveCodeset,
  };
};
